import React, { useState, useRef } from "react";
import { Plus } from "lucide-react";
import AddToBoardDropdown from "./AddToBoardDropdown";

/**
 * Inline "—— Add Content ——" divider that opens the add-content popup.
 *
 * @param {"end" | "inline"} variant — kept for API compatibility; both render the divider style
 */
export default function AddContentBlock({ insertIndex, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  // Anchor the dropdown to the pill button so it opens near the click target
  const pillRef = useRef(null);

  const handleSelect = (id) => {
    setOpen(false);
    onSelect?.(id, insertIndex);
  };

  return (
    <div className="w-full py-3" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen((prev) => !prev); }}
        className="flex items-center gap-3 w-full cursor-pointer group/add select-none"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {/* Left dashed line */}
        <div
          className="flex-1 h-px transition-all group-hover/add:opacity-80"
          style={{
            background: "repeating-linear-gradient(to right, var(--border-default) 0, var(--border-default) 5px, transparent 5px, transparent 10px)",
          }}
        />

        {/* Centre pill: anchors the dropdown */}
        <div
          ref={pillRef}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all shrink-0 group-hover/add:shadow-sm"
          style={{
            borderColor: open ? "var(--brand-primary)" : "var(--border-default)",
            color: open ? "var(--brand-primary)" : "var(--text-muted)",
            backgroundColor: open ? "color-mix(in srgb, var(--brand-primary) 8%, transparent)" : "transparent",
          }}
        >
          <Plus
            className="w-3.5 h-3.5 transition-transform duration-200 group-hover/add:rotate-90"
            style={{ color: "currentColor" }}
            strokeWidth={2.5}
          />
          <span className="text-xs font-semibold tracking-wide" style={{ color: "currentColor" }}>
            Add Content
          </span>
        </div>

        {/* Right dashed line */}
        <div
          className="flex-1 h-px transition-all group-hover/add:opacity-80"
          style={{
            background: "repeating-linear-gradient(to right, var(--border-default) 0, var(--border-default) 5px, transparent 5px, transparent 10px)",
          }}
        />
      </div>

      <AddToBoardDropdown
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={pillRef}
        onSelectType={handleSelect}
      />
    </div>
  );
}
